/*
  EU harmonised notice on the legal guarantee of conformity, shown in a modal dialog.

  The Commission's practical guidelines (Ares(2026)4331985, section 2.3) show the notice
  appearing in full on the first click of a line such as "Your legal guarantee rights".
  One dialog serves every trigger on the page: product form, cart page and cart drawer.
*/

class EuWarrantyNoticeDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.dialog.addEventListener('click', this.onClick);
    this.dialog.addEventListener('keydown', this.onKeyDown);
    this.dialog.addEventListener('close', this.onClose);
  }

  disconnectedCallback() {
    if (!this.dialog) return;

    this.dialog.removeEventListener('click', this.onClick);
    this.dialog.removeEventListener('keydown', this.onKeyDown);
    this.dialog.removeEventListener('close', this.onClose);
  }

  open(opener) {
    if (!this.dialog || this.dialog.open) return;

    this.opener = opener;
    this.renderImage();
    this.dialog.showModal();
  }

  /*
    The notice ships inside a <template> so its SVG, several hundred kilobytes, is only
    requested once someone asks to read it. `loading="lazy"` is not enough: Chrome
    fetches lazy images inside a closed dialog.
  */
  renderImage() {
    const template = this.dialog.querySelector('template[data-eu-warranty-image]');
    if (template) template.replaceWith(template.content.cloneNode(true));
  }

  onClick = (event) => {
    // The panel fills the dialog box, so a click that targets the dialog itself landed on
    // the backdrop.
    if (event.target === this.dialog || event.target.closest('[data-eu-warranty-close]')) {
      this.dialog.close();
    }
  };

  /*
    The browser closes the dialog on the Escape keydown and focus returns to the trigger
    before the matching keyup fires. Inside the cart drawer that keyup would reach the
    drawer's own Escape handler and close it as well, so exactly that one keyup is stopped.
  */
  onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    window.addEventListener('keyup', this.onEscapeKeyUp, { capture: true });
  };

  onEscapeKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    window.removeEventListener('keyup', this.onEscapeKeyUp, { capture: true });
  };

  // Safari does not focus a button on mouse click, so the browser may have nothing to
  // restore focus to. Returning it explicitly keeps keyboard and screen reader users in place.
  onClose = () => {
    if (this.opener && this.opener.isConnected) this.opener.focus();
    this.opener = null;
  };
}

class EuWarrantyNoticeOpener extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (this.button) this.button.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.onClick);
  }

  onClick = () => {
    const dialog = document.querySelector('eu-warranty-notice-dialog');
    if (dialog) dialog.open(this.button);
  };
}

if (!customElements.get('eu-warranty-notice-dialog')) {
  customElements.define('eu-warranty-notice-dialog', EuWarrantyNoticeDialog);
}

if (!customElements.get('eu-warranty-notice-opener')) {
  customElements.define('eu-warranty-notice-opener', EuWarrantyNoticeOpener);
}
